import { useRef } from 'react';
import { FilePlus, SquareHalf } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import {
  useBreakpoint,
  useCodeEditorActions,
  useCreateEditorThread,
  useEditorState,
  useInsertCodeToEditor,
  useInsertTextToEditor,
  useQueryParams,
  useRightSidePanelState,
  useUniqueId,
} from 'src/hooks';
import {
  ArtifactType,
  ArtifactMarkdownV3,
  ArtifactCodeV3,
  ProgrammingLanguageOptions,
  Message,
  ProgrammingLanguage,
} from 'src/types';
import {
  buildFullMarkdown,
  buildFullMarkdownFromMessage,
  getDocumentContentFromMessage,
  getLanguageKey,
} from 'src/utils';
import { Button } from 'src/v2/commonComponents/Button';
import {
  DrawerTooltipContainer,
  type DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import { MoveToEditorToggle } from '../MoveToEditorToggle';
import styles from './MoveToEditorTool.module.scss';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import { useChatHistory } from 'src/hooks';

type MoveToEditorCodeTool = {
  componentType: ArtifactType.CODE;
  codeContent: string;
  language: ProgrammingLanguage;
  activeTab?: never;
  disabled?: boolean;
  message?: never;
  taskTitle?: string;
};

type MoveToEditorDocumentTool = {
  message: Message;
  activeTab: string;
  disabled?: boolean;
  componentType: ArtifactType.TEXT;
  codeContent?: never;
  language?: never;
  taskTitle?: string;
};

type MoveToEditorToolProps = MoveToEditorCodeTool | MoveToEditorDocumentTool;

export const MoveToEditorTool = ({
  message,
  activeTab,
  disabled,
  componentType,
  codeContent,
  language,
  taskTitle,
}: MoveToEditorToolProps) => {
  const { isNinjaRetired } = useChatHistory();
  const { isMobileOrTablet } = useBreakpoint();
  const { setSearchParams } = useQueryParams();

  const { isEditorPanelExpanded } = useRightSidePanelState();
  const { insertTextToEditor } = useInsertTextToEditor();
  const { insertCodeToEditor } = useInsertCodeToEditor();
  const { editorType, codeEditorData } = useEditorState();
  const { setCodeEditorData } = useCodeEditorActions();

  const { onCreateEditorThread, isCreateEditorThreadLoading } =
    useCreateEditorThread();

  const drawerRef = useRef<DrawerTooltipContainerRef>(null);
  const { uniqueId } = useUniqueId();

  const tooltipAnchorId = `move-to-editor-${uniqueId}-tooltip`;

  // TODO(olha): WIP Editor add support adding footnotes and save to the database
  const handleOpenedFileClick = async () => {
    drawerRef.current?.onVisibilityToggle();

    if (componentType === ArtifactType.TEXT) {
      const { markdown, footnotes } = getDocumentContentFromMessage({
        message,
        activeTab,
      });

      const fullMarkdown = buildFullMarkdown({ markdown, footnotes });

      insertTextToEditor({
        markdown: fullMarkdown,
      });

      return;
    }

    if (componentType === ArtifactType.CODE) {
      // TODO(olha): WIP Editor check if we need update the state
      setCodeEditorData({
        language,
      });

      insertCodeToEditor({ codeContent });

      return;
    }
  };

  const createArtifact = async ({
    artifactContent,
    artifactType,
  }: {
    artifactContent: ArtifactMarkdownV3 | ArtifactCodeV3;
    artifactType: ArtifactType;
  }) => {
    const result = await onCreateEditorThread({
      artifactContent,
      artifactType,
    });

    if (!result?.thread?.thread_id) {
      return;
    }

    const { thread_id } = result.thread;
    setSearchParams({ editor: thread_id }, { replace: true });
    drawerRef.current?.onVisibilityToggle();
  };

  const handleNewDocumentClick = async () => {
    if (componentType !== ArtifactType.TEXT) {
      return;
    }

    const fullMarkdown = buildFullMarkdownFromMessage({
      message,
      activeTab,
    });

    const artifactContent: ArtifactMarkdownV3 = {
      fullMarkdown,
      title: taskTitle,
      type: ArtifactType.TEXT,
    };

    await createArtifact({
      artifactContent,
      artifactType: ArtifactType.TEXT,
    });
  };

  const handleNewCodeClick = async () => {
    if (componentType !== ArtifactType.CODE) {
      return;
    }

    const data = {
      content: codeContent,
      language: getLanguageKey(language),
    };

    const artifactContent: ArtifactCodeV3 = {
      language: data.language as ProgrammingLanguageOptions,
      code: data.content,
      type: ArtifactType.CODE,
    };

    await createArtifact({
      artifactContent,
      artifactType: ArtifactType.CODE,
    });
  };

  const isAddToOpenedFileDisable =
    !isEditorPanelExpanded ||
    editorType !== componentType ||
    (editorType === ArtifactType.CODE &&
      !!(
        codeEditorData?.content &&
        getLanguageKey(language) !== codeEditorData?.language
      ));

  if (isNinjaRetired) {
    return null;
  }

  if (isMobileOrTablet) {
    return null;
  }

  const place = componentType === ArtifactType.TEXT ? 'top' : 'left';

  return (
    <DrawerTooltipContainer<{ componentType: ArtifactType }>
      ref={drawerRef}
      id={tooltipAnchorId}
      place={place}
      offset={8}
      ToggleButton={MoveToEditorToggle}
      disabledToggle={disabled}
      onlyTooltip
      toggleButtonProps={{ componentType }}
    >
      <div className={styles.root}>
        <div className={styles.title}>Send to edit</div>

        <Button
          fullWidth
          color="transparent"
          onClick={
            componentType === ArtifactType.TEXT
              ? handleNewDocumentClick
              : handleNewCodeClick
          }
          disabled={isCreateEditorThreadLoading}
        >
          {isCreateEditorThreadLoading ? (
            <SpinnerSimple size={SVG_SIZE_M} />
          ) : (
            <FilePlus size={SVG_SIZE_M} />
          )}
          New file
        </Button>

        <Button
          color="transparent"
          disabled={isAddToOpenedFileDisable}
          onClick={handleOpenedFileClick}
          fullWidth
        >
          <SquareHalf size={SVG_SIZE_M} />
          Opened file
        </Button>
      </div>
    </DrawerTooltipContainer>
  );
};
